import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getMetaDescription, getPageTitle } from '../utils/metaDescriptions';

const SEOHead = ({ 
  title = null, 
  description = null, 
  image = null, 
  keywords = null,
  author = null,
  type = 'website',
  robots = 'index, follow'
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Get dynamic title and description if not provided
  const pageTitle = title || getPageTitle(currentPath);
  const pageDescription = description || getMetaDescription(currentPath);
  
  // Default values
  const defaultImage = 'https://www.ak-consultants.in/img/New-logo.png';
  const defaultKeywords = 'AK Consultants, AI, Machine Learning, GIS, Geospatial Intelligence, Biomedical Engineering, Mobile App Development, Full Stack Development, Technology Solutions, Innovation';
  const canonicalUrl = `https://www.ak-consultants.in${currentPath}`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />
      {author && <meta name="author" content={author} />}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="AK Consultants" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image || defaultImage} />
      
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      
      {/* Structured Data for Organization */}
      {currentPath === '/' && (
        <>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "AK Consultants",
              "alternateName": "Ace Keystone Consultants",
              "url": "https://www.ak-consultants.in",
              "logo": "https://www.ak-consultants.in/img/New-logo.png",
              "description": "Ace Keystone Consultants deliver cutting-edge innovative solutions in AI, geospatial intelligence, biomedical engineering, mobile app development, and smart infrastructure.",
              "foundingDate": "2022",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Coimbatore",
                "addressRegion": "Tamil Nadu",
                "addressCountry": "India"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer service",
                "url": "https://www.ak-consultants.in/contact"
              },
              "sameAs": [
                "https://www.linkedin.com/company/ak-consultants"
              ],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://www.ak-consultants.in/"
              },
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://www.ak-consultants.in/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            })}
          </script>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "AK Consultants",
              "url": "https://www.ak-consultants.in",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://www.ak-consultants.in/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              },
              "mainEntity": {
                "@type": "Organization",
                "@id": "https://www.ak-consultants.in/#organization"
              },
              "hasPart": [
                {
                  "@type": "WebPage",
                  "@id": "https://www.ak-consultants.in/about",
                  "name": "About Us",
                  "url": "https://www.ak-consultants.in/about"
                },
                {
                  "@type": "WebPage", 
                  "@id": "https://www.ak-consultants.in/services",
                  "name": "Our Services",
                  "url": "https://www.ak-consultants.in/services"
                },
                {
                  "@type": "WebPage",
                  "@id": "https://www.ak-consultants.in/ourtech", 
                  "name": "Our Tech",
                  "url": "https://www.ak-consultants.in/ourtech"
                },
                {
                  "@type": "WebPage",
                  "@id": "https://www.ak-consultants.in/careers",
                  "name": "Careers", 
                  "url": "https://www.ak-consultants.in/careers"
                },
                {
                  "@type": "WebPage",
                  "@id": "https://www.ak-consultants.in/contact",
                  "name": "Contact Us",
                  "url": "https://www.ak-consultants.in/contact"
                }
              ]
            })}
          </script>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SiteNavigationElement",
              "name": "Main Navigation",
              "url": "https://www.ak-consultants.in",
              "hasPart": [
                {
                  "@type": "SiteNavigationElement",
                  "name": "About Us",
                  "description": "Learn about AK Consultants - innovative technology solutions since 2022",
                  "url": "https://www.ak-consultants.in/about"
                },
                {
                  "@type": "SiteNavigationElement", 
                  "name": "Our Services",
                  "description": "Comprehensive AI, GIS, biomedical engineering, and development services",
                  "url": "https://www.ak-consultants.in/services"
                },
                {
                  "@type": "SiteNavigationElement",
                  "name": "Our Tech",
                  "description": "Cutting-edge technologies and frameworks powering our solutions",
                  "url": "https://www.ak-consultants.in/ourtech"
                },
                {
                  "@type": "SiteNavigationElement",
                  "name": "Careers", 
                  "description": "Join our team - exciting opportunities in AI, engineering, and technology",
                  "url": "https://www.ak-consultants.in/careers"
                },
                {
                  "@type": "SiteNavigationElement",
                  "name": "Contact Us",
                  "description": "Get in touch for innovative technology solutions and services",
                  "url": "https://www.ak-consultants.in/contact"
                }
              ]
            })}
          </script>
        </>
      )}
    </Helmet>
  );
};

export default SEOHead;